import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface SystemNotification {
  id: string;
  notification_type: string;
  reference_id: string | null;
  title: string;
  message: string | null;
  link_url: string | null;
  is_read: boolean;
  created_at: string;
}

/**
 * Hook to fetch system notifications for the current user
 */
export const useSystemNotifications = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["system-notifications", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("system_notifications")
        .select("*")
        .eq("user_id", user.id)
        .neq("notification_type", "outage_notification")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching system notifications:", error);
        throw error;
      }

      return data as SystemNotification[];
    },
    enabled: !!user?.id,
  });
};

/**
 * Hook to fetch unread system notifications count
 */
export const useUnreadSystemNotificationsCount = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["system-notifications-unread-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from("system_notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false)
        .neq("notification_type", "outage_notification");

      if (error) {
        console.error("Error fetching unread notifications count:", error);
        throw error;
      }

      return count || 0;
    },
    enabled: !!user?.id,
  });
};

/**
 * Hook to mark a notification as read
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("system_notifications")
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["system-notifications-unread-count", user?.id] });
    },
  });
};

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) return;

      const { error } = await supabase
        .from("system_notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false)
        .neq("notification_type", "outage_notification");

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["system-notifications-unread-count", user?.id] });
    },
  });
};
